import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import TourPrompt from './TourPrompt';
import GuidedTourCard from './GuidedTourCard';
import { agentTourChoiceStored, agentTourStorageKey, readStoredFlag, writeStoredFlag } from '../utils/tourPrompt';
import { stepsForProfile } from '../shared/guidedTour';
import { isWebApp } from '../utils/platform';

const OnboardingTour = ({ profile, onComplete, onShowStep, replaySignal = 0 }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(() => (
    agentTourChoiceStored(profile, readStoredFlag(
      typeof localStorage !== 'undefined' ? localStorage : null,
      agentTourStorageKey(profile && profile.id)
    ))
  ));
  const lastReplay = useRef(replaySignal);
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const steps = stepsForProfile(profile, { web: isWebApp(), phone });
  const step = steps[Math.min(currentStep, Math.max(steps.length - 1, 0))] || null;

  useEffect(() => {
    const stored = readStoredFlag(localStorage, agentTourStorageKey(profile && profile.id));
    setPromptDismissed(agentTourChoiceStored(profile, stored));
  }, [profile]);

  useEffect(() => {
    const onResize = () => setPhone(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!replaySignal || replaySignal === lastReplay.current) return;
    lastReplay.current = replaySignal;
    setCurrentStep(0);
    setShowTour(true);
  }, [replaySignal]);

  useEffect(() => {
    if (!showTour || !step || !onShowStep) return;
    onShowStep(step);
  }, [showTour, step, onShowStep]);

  const dismissPrompt = async () => {
    writeStoredFlag(localStorage, agentTourStorageKey(profile && profile.id));
    setPromptDismissed(true);
    setShowTour(false);
    if (profile && profile.id) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ tour_completed: true })
          .eq('id', profile.id);
        if (!error) profile.tour_completed = true;
      } catch (error) {
        console.error('Error saving tour dismissal:', error);
      }
    }
    if (onComplete) onComplete();
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      dismissPrompt();
    }
  };

  if (!showTour) {
    if (promptDismissed || !profile || !profile.id) return null;
    return (
      <TourPrompt
        detail="A short walkthrough of the dashboard. You can skip it and open it later from More."
        onStart={() => {
          setCurrentStep(0);
          setShowTour(true);
        }}
        onSkip={dismissPrompt}
      />
    );
  }

  return (
    <GuidedTourCard
      step={step}
      index={currentStep}
      total={steps.length}
      onNext={handleNext}
      onBack={() => setCurrentStep((value) => Math.max(0, value - 1))}
      onSkip={dismissPrompt}
    />
  );
};

export default OnboardingTour;
